import { lazy } from 'react'

import ProtectedRoute from '@/components/protected-route'
import { sentryCreateBrowserRouter } from '@/lib/sentry'

const HomePage = lazy(() => import('@/pages/home'))
const SignIn = lazy(() => import('@/pages/sign-in'))
const SignUp = lazy(() => import('@/pages/sign-up'))
const SignOut = lazy(() => import('@/pages/sign-out'))
const LibraryPage = lazy(() => import('@/pages/library'))
const BookPage = lazy(() => import('@/pages/book'))
const BookCreatePage = lazy(() => import('@/pages/book-create'))
const BookDetailPage = lazy(() => import('@/pages/book-detail'))
const ShelfPage = lazy(() => import('@/pages/shelf'))
const TagPage = lazy(() => import('@/pages/tag'))
const LoanPage = lazy(() => import('@/pages/loan'))
const MemberPage = lazy(() => import('@/pages/member'))
const MemberDetailPage = lazy(() => import('@/pages/member-detail'))
const MemberCreatePage = lazy(() => import('@/pages/member-create'))
const RolePage = lazy(() => import('@/pages/role'))
const BillingPage = lazy(() => import('@/pages/billing'))
const SettingPage = lazy(() => import('@/pages/setting'))
const SettingImportPage = lazy(() => import('@/pages/setting/import'))
const SettingSystemPage = lazy(() => import('@/pages/setting/system'))
const TrashPage = lazy(() => import('@/pages/trash'))
const ErrorPage = lazy(() => import('@/pages/error'))
const CallbackPage = lazy(() => import('@/pages/callback'))
const MainLayout = lazy(() => import('@/layouts/main'))
const AuthLayout = lazy(() => import('@/layouts/auth'))
const AuthBaseLayout = lazy(() => import('@/layouts/auth-base'))
const OTPPage = lazy(() => import('@/pages/otp'))
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'))
const PasswordChangedPage = lazy(() => import('@/pages/password-changed'))
const ResetPasswordPage = lazy(() => import('@/pages/reset-password'))
const WishlistPage = lazy(() => import('@/pages/wishlist'))
const WishlistDetailPage = lazy(() => import('@/pages/wishlist-detail'))
const ShelfDetailPage = lazy(() => import('@/pages/shelf-detail'))
const ShelfGroupPage = lazy(() => import('@/pages/shelf/group'))
const ItemDetailPage = lazy(() => import('@/pages/item-detail'))
const TagDetailPage = lazy(() => import('@/pages/tag-detail'))
const OrganizationPage = lazy(() => import('@/pages/organization'))
const OrganizationEditPage = lazy(() => import('@/pages/organization/edit'))
const SearchOnlinePage = lazy(() => import('@/pages/search-online'))
const RoleCreatePage = lazy(() => import('@/pages/role-create'))
const RoleDetailPage = lazy(() => import('@/pages/role-detail'))
const AccountPage = lazy(() => import('@/pages/account'))
const AccountProfilePage = lazy(() => import('@/pages/account/profile'))
const MyQRPage = lazy(() => import('@/pages/account/my-qr'))
const ChangePasswordPage = lazy(() => import('@/pages/account/change-password'))
const LoanDetailPage = lazy(() => import('@/pages/loan-detail'))
const NotificationPage = lazy(() => import('@/pages/notification'))
const SwitchTenantPage = lazy(() => import('@/pages/switch-tenant'))
const AcceptInvitationPage = lazy(() => import('@/pages/accept-invitation'))
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'))

export const ROUTES = {
  HOME: '/',
  SIGN_UP: '/sign-up',
  SIGN_IN: '/sign-in',
  SIGN_OUT: '/sign-out',
  FORGOT_PASSWORD: '/forgot-password',
  OTP: '/verify-otp',
  RESET_PASSWORD: '/reset-password',
  PASSWORD_CHANGED: '/password-changed',
  LOAN: '/loans',
  MEMBER: '/members',
  ROLE: '/roles',
  BILLING: '/billings',
  SETTING: '/settings',
  TRASH: '/trash',
  LIBRARY: '/libraries',
  CALL_BACK: '/callback',
  WISHLIST: '/wishlist',
  BOOK: '/books',
  SHELF: '/shelves',
  SHELF_GROUP: '/shelf-groups',
  ITEM: '/items',
  TAG: '/tags',
  ORGANIZATION: '/organization',
  ORGANIZATION_EDIT: '/organization/edit',
  ACCOUNT: '/account',
  NOTIFICATION: '/notification',
  SWITCH_TENANT: '/switch-tenant',
  ACCEPT_INVITATION: '/acceptinvitation',
  PRICACY_POLICY: '/privacy-policy',
}

export const router = sentryCreateBrowserRouter([
  {
    path: ROUTES.HOME,
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTES.LIBRARY,
        children: [
          {
            index: true,
            element: <LibraryPage />,
          },
          {
            path: ':libraryId',
            children: [
              {
                path: 'books',
                children: [
                  { index: true, element: <BookPage /> },
                  { path: 'create', element: <BookCreatePage /> },
                  { path: 'search', element: <SearchOnlinePage /> },
                ],
              },
              {
                path: 'shelves',
                element: <ShelfPage />,
              },
              {
                path: 'tags',
                element: <TagPage />,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES.BOOK,
        children: [{ path: ':titleKey', element: <BookDetailPage /> }],
      },
      {
        path: ROUTES.SHELF,
        children: [{ path: ':shelfKey', element: <ShelfDetailPage /> }],
      },
      {
        path: ROUTES.SHELF_GROUP,
        children: [{ path: ':shelfKey', element: <ShelfGroupPage /> }],
      },
      {
        path: ROUTES.ITEM,
        children: [{ path: ':itemKey', element: <ItemDetailPage /> }],
      },
      {
        path: ROUTES.TAG,
        children: [{ path: ':tagKey', element: <TagDetailPage /> }],
      },
      {
        path: ROUTES.LOAN,
        children: [
          {
            index: true,
            element: <LoanPage />,
          },
          {
            path: ':loanKey',
            element: <LoanDetailPage />,
          },
        ],
      },
      {
        path: ROUTES.WISHLIST,
        children: [
          { element: <WishlistPage />, index: true },
          { path: ':titleKey', element: <WishlistDetailPage /> },
        ],
      },
      {
        path: ROUTES.MEMBER,
        children: [
          { element: <MemberPage />, index: true },
          {
            path: ':memberKey',
            element: <MemberDetailPage />,
          },
          { path: 'create', element: <MemberCreatePage /> },
        ],
      },
      {
        path: ROUTES.ROLE,
        children: [
          { index: true, element: <RolePage /> },
          { path: 'create', element: <RoleCreatePage /> },
          { path: ':roleKey', element: <RoleDetailPage /> },
        ],
      },
      {
        path: ROUTES.BILLING,
        element: <BillingPage />,
      },
      {
        path: ROUTES.SETTING,
        children: [
          { element: <SettingPage />, index: true },
          {
            path: 'import',
            element: <SettingImportPage />,
          },
          { path: 'systems', element: <SettingSystemPage /> },
        ],
      },
      {
        path: ROUTES.TRASH,
        element: <TrashPage />,
      },
      { path: ROUTES.ORGANIZATION, element: <OrganizationPage /> },
      { path: ROUTES.ORGANIZATION_EDIT, element: <OrganizationEditPage /> },
      {
        path: ROUTES.ACCOUNT,
        children: [
          { index: true, element: <AccountPage /> },
          { path: 'profile', element: <AccountProfilePage /> },
          { path: 'change-password', element: <ChangePasswordPage /> },
          { path: 'my-qr', element: <MyQRPage /> },
        ],
      },
      {
        path: ROUTES.NOTIFICATION,
        element: <NotificationPage />,
      },
    ],
  },
  {
    element: <AuthBaseLayout />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTES.SIGN_UP,
            element: <SignUp />,
          },
          {
            path: ROUTES.SIGN_IN,
            element: <SignIn />,
          },
        ],
      },
      {
        path: ROUTES.OTP,
        element: <OTPPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },
      {
        path: ROUTES.PASSWORD_CHANGED,
        element: <PasswordChangedPage />,
      },
    ],
  },
  { path: `${ROUTES.SWITCH_TENANT}/:tenantId`, element: <SwitchTenantPage /> },
  {
    path: ROUTES.CALL_BACK,
    element: <CallbackPage />,
  },
  {
    path: ROUTES.SIGN_OUT,
    element: <SignOut />,
  },
  {
    path: ROUTES.ACCEPT_INVITATION,
    element: <AcceptInvitationPage />,
  },
  {
    path: ROUTES.PRICACY_POLICY,
    element: <PrivacyPolicyPage />,
  },
])

interface Route {
  path: string
  label: string
}

export const footerLinks: Route[] = [
  {
    path: 'https://bookshare.app',
    label: 'about_us',
  },
  {
    path: ROUTES.SIGN_UP,
    label: 'sign_up',
  },
  {
    path: ROUTES.SIGN_IN,
    label: 'sign_in',
  },
  {
    path: 'https://skoolib.notion.site/',
    label: 'help',
  },
  {
    path: 'https://bookshare.app/blog',
    label: 'blog',
  },
  {
    path: ROUTES.PRICACY_POLICY,
    label: 'privacy_policy',
  },
  {
    path: 'https://bookshare.app/en/contact',
    label: 'contact',
  },
]
